import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import { httpClient } from "api/httpClient";
import { useSimpleModalToggle } from "components/dialogs/useSimpleModal";
import { isAbortError, isTimeoutError, useAbortableRequest } from "hooks/useAbortableRequest";
import React, { useCallback, useEffect, useState } from "react";
import { useNotify, useRefresh, useTranslate } from "react-admin";
import { useNotifyError } from "utils/notifiers/useNotifyError";

import { PdfExtractionPreviewDialog } from "./PdfExtractionPreviewDialog";
import { PDF_EXTRACTION_TIMEOUT_MS } from "./pdfExtractionTimeouts";

const UNAVAILABLE_ON_LOOKUP_FAILURE = { is_available: false, reason: null };

export const PalletsFromPdfButton = ({ order, disabled }) => {
	const { open, handleOpen, handleClose } = useSimpleModalToggle();
	const notify = useNotify();
	const notifyError = useNotifyError();
	const translate = useTranslate();
	const refresh = useRefresh();
	const [availability, setAvailability] = useState(null);
	const [preview, setPreview] = useState(null);
	const [loading, setLoading] = useState(false);

	const request = useAbortableRequest();

	const orderId = order?.id;

	useEffect(() => {
		if (!orderId) return undefined;

		const controller = new AbortController();
		httpClient(`/orders/${orderId}/pallets-from-pdf/availability`, {
			signal: controller.signal,
		})
			.then(({ json }) => {
				if (!controller.signal.aborted) setAvailability(json);
			})
			.catch((error) => {
				if (!isAbortError(error)) setAvailability(UNAVAILABLE_ON_LOOKUP_FAILURE);
			});

		return () => controller.abort();
	}, [orderId]);

	const handleExtract = useCallback(async () => {
		setLoading(true);
		try {
			const { json } = await request(`/orders/${orderId}/pallets-from-pdf/preview`, {
				method: "POST",
				body: JSON.stringify({}),
				timeoutMs: PDF_EXTRACTION_TIMEOUT_MS,
			});
			setPreview(json);
			handleOpen();
		} catch (error) {
			if (isTimeoutError(error)) {
				notifyError(translate("osfc.message.warn.pdf_extraction_timed_out"));
			} else if (!isAbortError(error)) {
				notifyError(error);
			}
		} finally {
			setLoading(false);
		}
	}, [orderId, handleOpen, notifyError, request, translate]);

	const handleSaved = useCallback(() => {
		handleClose();
		setPreview(null);
		notify(translate("osfc.dialogs.generate-pallets-from-pdf.success"), "success");
		refresh();
	}, [handleClose, notify, translate, refresh]);

	const isDisabled = disabled || loading || !availability?.is_available;

	const button = (
		<span>
			<Button
				size="small"
				variant="contained"
				color="primary"
				disabled={isDisabled}
				onClick={handleExtract}
			>
				{loading && <CircularProgress size={18} thickness={2} style={{ marginRight: 8 }} />}
				{loading
					? translate("osfc.dialogs.generate-pallets-from-pdf.loading")
					: translate("osfc.buttons.generate-pallets-from-pdf")}
			</Button>
		</span>
	);

	return (
		<>
			{availability?.reason ? <Tooltip title={availability.reason}>{button}</Tooltip> : button}
			{open && preview && (
				<PdfExtractionPreviewDialog
					open={open}
					onClose={handleClose}
					order={order}
					preview={preview}
					onSaved={handleSaved}
				/>
			)}
		</>
	);
};
